import * as THREE from "three";
import { BaseManager } from "../base/BaseManager";
import { Context } from "../core/context";
import { DBEntity, DbLine, DbMesh, FMathUtils } from "@/CadEngine";
import { CommonColor, SampleLayerType, Linearity } from "@/core/Parameters";
import { generateUUID } from "three/src/math/MathUtils";
import { IndserObject, IndsertPosition } from "@/core/IndserObject";
/**
 * 工具管理
 */
class ToolsManger extends BaseManager {
  constructor(context: Context) {
    super(context);
  }
  // 求两条线的交点
  public CheckLineIntersection(line1: DbLine, line2: DbLine) {
    let denominator,
      a,
      b,
      numerator1,
      numerator2,
      result: {
        x: number | null;
        y: number | null;
        onLine1: boolean;
        onLine2: boolean;
      } = {
        x: null,
        y: null,
        onLine1: false,
        onLine2: false,
      };

    let line1StartPoint = new THREE.Vector3(
      line1.points![0],
      line1.points![1],
      line1.points![2]
    );
    let line1EndPoint = new THREE.Vector3(
      line1.points![3],
      line1.points![4],
      line1.points![5]
    );
    let line2StartPoint = new THREE.Vector3(
      line2.points![0],
      line2.points![1],
      line2.points![2]
    );
    let line2EndPoint = new THREE.Vector3(
      line2.points![3],
      line2.points![4],
      line2.points![5]
    );

    denominator =
      (line2EndPoint.y - line2StartPoint.y) *
        (line1EndPoint.x - line1StartPoint.x) -
      (line2EndPoint.x - line2StartPoint.x) *
        (line1EndPoint.y - line1StartPoint.y);

    // 如果为0则平行
    if (denominator == 0) {
      return result;
    }

    a = line1StartPoint.y - line2StartPoint.y;
    b = line1StartPoint.x - line2StartPoint.x;
    numerator1 =
      (line2EndPoint.x - line2StartPoint.x) * a -
      (line2EndPoint.y - line2StartPoint.y) * b;
    numerator2 =
      (line1EndPoint.x - line1StartPoint.x) * a -
      (line1EndPoint.y - line1StartPoint.y) * b;
    a = numerator1 / denominator;
    b = numerator2 / denominator;

    result.x = line1StartPoint.x + a * (line1EndPoint.x - line1StartPoint.x);
    result.y = line1StartPoint.y + a * (line1EndPoint.y - line1StartPoint.y);
    if (a > 0 && a < 1) {
      result.onLine1 = true;
    }
    if (b > 0 && b < 1) {
      result.onLine2 = true;
    }

    return result;
  }

  // 检测传入的是否是圆形
  public CheckIsCircle(data: DbLine | DbMesh) {
    let returnData: { matrix: THREE.Matrix; isCircle: boolean } = {
      matrix: new THREE.Matrix4(),
      isCircle: false,
    };
    if (!(data instanceof DbLine || data instanceof DbMesh)) {
      return console.error("传入的数据不是线段或者mesh");
    }
    let center = new THREE.Vector3(
      data.sphere!.center.x,
      data.sphere!.center.y,
      data.sphere!.center.z
    );
    let radius = data.sphere!.radius;
    let distances: number[] = [];
    let length = 0;
    returnData.matrix = new THREE.Matrix4().setPosition(center);
    if (data instanceof DbLine) {
      let points = data.points!;
      length = points!.length;
      for (let i = 0; i < points.length; i += 3) {
        let x = points[i];
        let y = points[i + 1];
        let z = points[i + 2];
        let vec = new THREE.Vector3(x, y, z);
        let distance = vec.distanceTo(center);
        distances.push(distance);
      }
      if (
        data.root_reference_id &&
        this.m_context.ModelManager.getInstancesMatrix(data.root_reference_id!)!
      ) {
        returnData.matrix = this.m_context.ModelManager.getInstancesMatrix(
          data.root_reference_id!
        )!.matrix;
      }
    } else if (data instanceof DbMesh) {
      let vertexs = data.vertexs!;
      length = vertexs.length;
      for (let i = 0; i < vertexs.length; i += 3) {
        let x = vertexs[i];
        let y = vertexs[i + 1];
        let z = vertexs[i + 2];
        let vec = new THREE.Vector3(x, y, z);
        let distance = vec.distanceTo(center);
        distances.push(distance);
      }
      if (
        data.root_reference_id &&
        this.m_context.ModelManager.getInstancesMatrix(data.root_reference_id!)!
      ) {
        returnData.matrix = this.m_context.ModelManager.getInstancesMatrix(
          data.root_reference_id!
        )!.matrix;
      }
    }
    let max = Math.max(...distances);
    let min = Math.min(...distances);
    let boxLone = data.box!.max.x - data.box!.min.x;
    let boxWidth = data.box!.max.y - data.box!.min.y;
    if (length > 18) {
      if (boxLone == 0 || boxWidth == 0 || Math.abs(boxLone - boxWidth) > 1) {
        return returnData;
      }
      if (max - min !== 0 && max - min < radius * 0.01) {
        returnData.isCircle = true;
      }
    }

    return returnData;
  }

  // 根据给定的旋转度数旋转传入的对象
  public RotateObject(objs: DbLine[] | DbMesh[], degree: number) {
    let { Lines, Meshs } = this.m_context.ModelManager;
    let objsArr: any[] = [{ center: undefined, data: [] }];
    let num = 0;
    for (let i = 0; i < objs.length; i++) {
      if (i === 0) {
        objsArr[num].data.push(objs[i]);
      }
      if (i > 0) {
        if (objs[i].root_reference_id === objs[i - 1].root_reference_id) {
          objs[i].sphere!.center = new THREE.Vector3(
            objs[i].sphere!.center.x,
            objs[i].sphere!.center.y,
            objs[i].sphere!.center.z
          );
          objsArr[num].data.push(objs[i]);
        } else {
          num++;
          objsArr[num] = { center: undefined, data: [objs[i]] };
        }
      }
    }
    for (let i = 0; i < objsArr.length; i++) {
      let vec = new THREE.Vector3();
      for (let j = 0; j < objsArr[i].data.length; j++) {
        vec.add(objsArr[i].data[j].sphere.center);
        if (j == objsArr[i].data.length - 1) {
          let center = vec.divideScalar(objsArr[i].data.length);
          objsArr[i].center = center;
        }
      }
    }
    for (let num = 0; num < objsArr.length; num++) {
      objsArr[num].data.forEach((item: DbLine | DbMesh) => {
        if (item instanceof DbLine) {
          for (let i = 0; i < item.points.length; i++) {
            if (i % 3 === 0) {
              let x = item.points[i];
              let y = item.points[i + 1];
              let z = item.points[i + 2];
              let vec = new THREE.Vector3(x, y, z);
              let subVec = vec.sub(objsArr[num].center);
              subVec.applyAxisAngle(
                new THREE.Vector3(0, 0, 1),
                THREE.MathUtils.degToRad(degree)
              );
              subVec.add(objsArr[num].center);
              item.points[i] = subVec.x;
              item.points[i + 1] = subVec.y;
              item.points[i + 2] = subVec.z;
            }
          }
          item.box = new THREE.Box3().setFromArray(item.points);
          item.sphere = item.box.getBoundingSphere(new THREE.Sphere());
          for (const key in Lines) {
            if (key.split("_")[0] === item.layerId) {
              this.LinesToModel(Lines[key].lines, Lines[key].model);
            }
          }
        }
        if (item instanceof DbMesh) {
          for (let i = 0; i < item.vertexs.length; i++) {
            if (i % 3 === 0) {
              let x = item.vertexs[i];
              let y = item.vertexs[i + 1];
              let z = item.vertexs[i + 2];
              let vec = new THREE.Vector3(x, y, z);
              let subVec = vec.sub(objsArr[num].center);
              subVec.applyAxisAngle(
                new THREE.Vector3(0, 0, 1),
                THREE.MathUtils.degToRad(degree)
              );
              subVec.add(objsArr[num].center);
              item.vertexs[i] = subVec.x;
              item.vertexs[i + 1] = subVec.y;
              item.vertexs[i + 2] = subVec.z;
            }
          }
          item.box = new THREE.Box3().setFromArray(item.vertexs);
          item.sphere = item.box.getBoundingSphere(new THREE.Sphere());
          for (const key in Meshs) {
            if (key.split("_")[0] === item.layerId) {
              this.MeshToModel(Meshs[key].meshs, Meshs[key].model);
            }
          }
        }
      });
      if (objsArr[num].data[0].root_reference_id) {
        let matrix = this.m_context.ModelManager.getInstancesMatrix(
          objsArr[num].data[0].root_reference_id
        )?.matrix;
        // 分解矩阵为位置、旋转和缩放的组件
        const position = new THREE.Vector3();
        const scale = new THREE.Vector3();
        const rotation = new THREE.Quaternion();
        matrix!.decompose(position, rotation, scale);
        const euler = new THREE.Euler().setFromQuaternion(rotation);
        const degrees = THREE.MathUtils.radToDeg(euler.z);
        rotation.setFromAxisAngle(
          new THREE.Vector3(0, 0, 1),
          THREE.MathUtils.degToRad(degree + degrees)
        );
        matrix!.compose(position, rotation, scale);
      }
    }
  }

  /**
   * @description 创建辅助虚线
   * @param {THREE.Vector3} lineStartPoint 起始点
   * @return {THREE.Line} 虚线对象
   */
  public CreatedAuxLine(color = CommonColor.Aux) {
    const geometry = new THREE.BufferGeometry();
    const matLine = this.m_context.MaterialManager.createDashedLineMaterial(
      new THREE.Color(color)
    );
    let auxLine = new THREE.Line(geometry, matLine);
    this.m_context.SceneManager.AuxModel.add(auxLine);
    auxLine.frustumCulled = false;
    return auxLine;
  }
  public CreatedLine(color = CommonColor.Aux) {
    const geometry = new THREE.BufferGeometry();
    const matLine = this.m_context.MaterialManager.createLineMaterial(
      new THREE.Color(color)
    );
    let auxLine = new THREE.Line(geometry, matLine);
    this.m_context.SceneManager.AuxModel.add(auxLine);
    auxLine.frustumCulled = false;
    return auxLine;
  }

  /**
   * @description 创建坐标线
   */
  public CreatedLocationLine(color = CommonColor.White) {
    let geometry = new THREE.BufferGeometry();
    let scale = this.m_context.ViewManager.ScreenModelScale;
    const matLine = this.m_context.MaterialManager.createLineMaterial(
      new THREE.Color(color)
    );
    let position = [-20, 0, 10, 20, 0, 10, 0, -20, 10, 0, 20, 10];
    geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(position), 3)
    );
    let locationLine = new THREE.LineSegments(geometry, matLine);
    locationLine.renderOrder = 20;
    locationLine.scale.set(scale, scale, scale);
    locationLine.userData.scale = { x: 1, y: 1, z: 1 };
    locationLine.frustumCulled = false;
    this.m_context.SceneManager.AuxModel.add(locationLine);
    return locationLine;
  }

  public RgbaToHex(color: string) {
    var values = color
      // .replace(/rgba?\(/, "")
      // .replace(/\)/, "")
      // .replace(/\s+/g, "")
      .split(",");
    let r = Math.max(0, Math.min(255, Number(values[0])));
    let g = Math.max(0, Math.min(255, Number(values[1])));
    let b = Math.max(0, Math.min(255, Number(values[2])));
    // 将 RGB 值转换为十六进制字符串，并确保每个分量都是两位数
    const rHex = r.toString(16).padStart(2, "0");
    const gHex = g.toString(16).padStart(2, "0");
    const bHex = b.toString(16).padStart(2, "0");
    // 转换为 16 进制并返回
    // 拼接成以 '0x' 开头的十六进制颜色代码
    return Number(`0x${rHex}${gHex}${bHex}`);
  }
  // 判断数据是否是规定类型
  public EstimateSelectType(
    selectObj: DBEntity,
    Type: SampleLayerType[] = [SampleLayerType.Line],
    linearity: Linearity[] = [Linearity.Continuous]
  ) {
    let layerName = this.m_context.ModelManager.GetLayerById(
      selectObj.layerId
    )?.Name;
    if (layerName) {
      let sampleData =
        this.m_context.ModelManager.GetSampleDataByLayerName(layerName);
      if (sampleData) {
        let { LayerType, LayerLineTypeName } = sampleData;
        let isLinearity, isLayerLineTypeName;
        for (let i = 0; i < linearity.length; i++) {
          const item = linearity[i];
          if (LayerLineTypeName.toLowerCase().includes(item.toLowerCase())) {
            isLinearity = true;
            break;
          }
        }
        for (let i = 0; i < Type.length; i++) {
          const item = Type[i];
          if (LayerType.toLowerCase().includes(item.toLowerCase())) {
            isLayerLineTypeName = true;
            break;
          }
        }
        if (isLinearity && isLayerLineTypeName) {
          return true;
        }
      }
    }
    return false;
  }

  //获取正交点
  public GetOrthogonalityPoint(
    curPoint: THREE.Vector3,
    prePoint: THREE.Vector3
  ): THREE.Vector3 {
    let xAxis = new THREE.Vector3(1, 0, 0);
    let zAxis = new THREE.Vector3(0, 0, 1);
    let vector = curPoint.clone().sub(prePoint);
    let tempVector = xAxis.clone();
    let vectorNormalize = vector.clone().normalize();
    let radian = vectorNormalize.angleTo(xAxis);
    if (vectorNormalize.clone().cross(xAxis).z > 0) {
      radian = Math.PI * 2 - radian;
    }

    if (radian > Math.PI / 4 && radian < (Math.PI / 4) * 3) {
      tempVector.applyAxisAngle(zAxis, Math.PI / 2);
    } else if (radian > (Math.PI / 4) * 3 && radian < (Math.PI / 4) * 5) {
      tempVector.applyAxisAngle(zAxis, Math.PI);
    } else if (radian > (Math.PI / 4) * 5 && radian < (Math.PI / 4) * 7) {
      tempVector.applyAxisAngle(zAxis, (Math.PI / 2) * 3);
    }
    let dotLength = vector.clone().dot(tempVector.clone());
    tempVector.setLength(dotLength);
    let orthogonalityPoint = prePoint.clone().add(tempVector);
    return orthogonalityPoint;
  }

  // 从lines和meshs中找到相同类型的数据
  public GetSameTypeData(dbEntitys: DBEntity[]) {
    let rootIds: string[] = [];
    let editLines: DbLine[] = [];
    let editMeshs: DbMesh[] = [];
    let { Lines, Meshs } = this.m_context.ModelManager;
    for (let i = 0; i < dbEntitys.length; i++) {
      const dbEntity = dbEntitys[i];
      if (
        dbEntity.root_reference_id &&
        !rootIds.includes(dbEntity.root_reference_id)
      ) {
        rootIds.push(dbEntity.root_reference_id);
        let lines =
          this.m_context.ModelManager.Group[dbEntity.root_reference_id].lines;
        let meshs =
          this.m_context.ModelManager.Group[dbEntity.root_reference_id].mesh;
        editLines.push(...lines);
        editMeshs.push(...meshs);
      } else if (!dbEntity.root_reference_id) {
        let key = this.getKey(dbEntity as DbLine);
        if (Lines[key]) {
          let { lines } = Lines[key];
          for (let j = 0; j < lines.length; j++) {
            let line = lines[j];
            if (
              line.block_path + line.id ==
              dbEntity.block_path + dbEntity.id
            ) {
              editLines.push(line);
            }
          }
        }
        if (Meshs[key]) {
          let { meshs } = Meshs[key];
          for (let j = 0; j < meshs.length; j++) {
            let mesh = meshs[j];
            if (
              mesh.block_path + mesh.id ==
              dbEntity.block_path + dbEntity.id
            ) {
              editMeshs.push(mesh);
            }
          }
        }
      }
    }
    return { editLines, editMeshs };
  }

  // 根据数据创建临时对象
  public PassDbEntityCreateTempModel(
    data: DbLine | DbMesh,
    color?: CommonColor
  ) {
    let { Lines, Meshs } = this.m_context.ModelManager;
    let key = this.getKey(data);
    if (data instanceof DbLine) {
      if (Lines[key]) {
        let { model } = Lines[key];
        let material = (model.material as THREE.LineBasicMaterial).clone();
        let geometry = new THREE.BufferGeometry();
        geometry.setAttribute(
          "position",
          new THREE.BufferAttribute(data.points!, 3)
        );
        geometry.setIndex([...data.indexes!]);
        if (color) {
          material.color.set(color);
        }
        const lineObject = new THREE.LineSegments(geometry, material);
        lineObject.userData = {
          key,
          originData: data,
        };
        return lineObject;
      }
    } else if (data instanceof DbMesh) {
      if (Meshs[key]) {
        let { model } = Meshs[key];
        let material = (model.material as THREE.MeshBasicMaterial).clone();
        let geometry = new THREE.BufferGeometry();
        geometry.setAttribute(
          "position",
          new THREE.BufferAttribute(data.vertexs!, 3)
        );
        if (color) {
          material.color.set(color);
        }

        const meshObject = new THREE.Mesh(geometry, material);
        meshObject.userData = {
          key,
          originData: data,
        };
        return meshObject;
      }
    }
  }

  // 将临时对象的数据赋给原始数据
  public PassTempModelDataToDbEntity(tempModels: THREE.Group) {
    for (let i = 0; i < tempModels.children.length; i++) {
      let tempModel = tempModels.children[i];
      if (
        tempModel instanceof THREE.LineSegments ||
        tempModel instanceof THREE.Mesh
      ) {
        let { key, originData } = tempModel.userData;
        let points = tempModel.geometry.attributes.position
          .array as Float32Array;
        if (originData instanceof DbLine) {
          originData.points = points;
        }
        if (originData instanceof DbMesh) {
          originData.vertexs = points;
        }
        originData.getBoundingBox();
        originData.getBoundingSphere();
      }
    }
  }

  public filterRepeatKeyData(tempModels: THREE.Group | (DbLine | DbMesh)[]) {
    let keyDatas: string[] = [];
    if (tempModels instanceof THREE.Group) {
      for (let i = 0; i < tempModels.children.length; i++) {
        let tempModel = tempModels.children[i] as
          | THREE.LineSegments
          | THREE.Mesh;
        let { key, originData } = tempModel.userData;
        if (!keyDatas.includes(key)) {
          keyDatas.push(key);
        }
      }
    } else {
      for (let i = 0; i < tempModels.length; i++) {
        let key = this.getKey(tempModels[i]);
        if (!keyDatas.includes(key)) {
          keyDatas.push(key);
        }
      }
    }

    return keyDatas;
  }

  // 通过点计算包围盒和包围球
  public PassPointsComputerBoxAndSphere(points: number[]) {
    let ps: THREE.Vector3[] = [];
    for (let i = 0; i < points.length; i += 3) {
      ps.push(new THREE.Vector3(points[i], points[i + 1], points[i + 2]));
    }

    let box = new THREE.Box3().setFromPoints(ps);
    let sphere = new THREE.Sphere();
    sphere.setFromPoints(ps);
    return { box, sphere };
  }

  public getBlockIdAndRootIds(datas: (DbLine | DbMesh)[]) {
    let blockIdAndRootIds: string[] = [];
    for (let i = 0; i < datas.length; i++) {
      let data = datas[i];
      if (data.blockId && data.root_reference_id) {
        let blockIdAndRootId = data.blockId + ":" + data.root_reference_id;
        if (!blockIdAndRootIds.includes(blockIdAndRootId)) {
          blockIdAndRootIds.push(blockIdAndRootId);
        }
      }
    }
    return blockIdAndRootIds;
  }
  public passBlockIdAndRootIdGetMatrix(blockIdAndRootId: string) {
    let blockId = blockIdAndRootId.split(":")[0];
    let rootId = blockIdAndRootId.split(":")[1];
    let block = this.m_context.ModelManager.Blocks[blockId];
    let matrix: undefined | THREE.Matrix4 = undefined;
    if (block && block.instances.length) {
      for (let i = 0; i < block.instances.length; i++) {
        let instance = block.instances[i];
        if (instance.id == rootId) {
          matrix = instance.matrix;
          this.m_context.ModelManager.setInstances(rootId, matrix);
          break;
        }
      }
    }
    return matrix;
  }

  public getExtendIds(datas: (DbLine | DbMesh)[]) {
    let extendIds: string[] = [];
    for (let i = 0; i < datas.length; i++) {
      let data = datas[i];
      if (data.root_reference_id) {
        for (const key in this.m_context.ModelManager.Group) {
          let instances = this.m_context.ModelManager.Group[key];
          if (key == data.root_reference_id) {
            if (instances.lines && instances.lines.length) {
              if (!extendIds.includes(instances.lines[0].extend_id!)) {
                extendIds.push(instances.lines[0].extend_id!);
              }
            } else if (instances.mesh && instances.mesh.length) {
              if (!extendIds.includes(instances.mesh[0].extend_id!)) {
                extendIds.push(instances.mesh[0].extend_id!);
              }
            }
          }
        }
      } else if (data.extend_id) {
        if (!extendIds.includes(data.extend_id)) {
          extendIds.push(data.extend_id);
        }
      }
    }
    return extendIds;
  }

  // 深拷贝数据
  public deepCopyDatas(datas: DBEntity[]) {
    let deepDatas: DBEntity[] = [];
    let { editLines, editMeshs } = this.GetSameTypeData(datas);
    for (let i = 0; i < editLines.length; i++) {
      let data = editLines[i];
      let newData = DbLine.clone(data);
      if (newData.root_reference_id) {
        let instanceData = this.m_context.ModelManager.Instances.get(
          newData.root_reference_id
        );
        if (instanceData) {
          if (!newData.userData) {
            newData.userData = {
              originMatrix: instanceData.matrix.clone(),
            };
          } else if (newData.userData instanceof Object) {
            newData.userData.originMatrix = instanceData.matrix.clone();
          }
        }
      }
      deepDatas.push(newData);
    }
    for (let i = 0; i < editMeshs.length; i++) {
      let data = editMeshs[i];
      let newData = DbMesh.clone(data);
      if (newData.root_reference_id) {
        let instanceData = this.m_context.ModelManager.Instances.get(
          newData.root_reference_id
        );
        if (instanceData) {
          if (!newData.userData) {
            newData.userData = {
              originMatrix: instanceData.matrix.clone(),
            };
          } else if (newData.userData instanceof Object) {
            newData.userData.originMatrix = instanceData.matrix.clone();
          }
        }
      }
      deepDatas.push(newData);
    }
    return deepDatas;
  }

  public getKey(data: DbLine | DbMesh) {
    let key = data.layerId + "_" + data.color;
    return key;
  }

  // 判断选中的数据里是否有相同类型的数据
  public judgeSelectDataIsIncludeSomeTypeData(dbEntity: DBEntity) {
    let selectData = this.m_context.SelectionManager.GetSelected();
    for (let i = 0; i < selectData.length; i++) {
      let data = selectData[i];
      if (data instanceof DbLine || data instanceof DbMesh) {
        if (dbEntity.root_reference_id) {
          if (data.root_reference_id == dbEntity.root_reference_id) {
            this.m_context.SelectionManager.RemoveSelect(data);
            return true;
          }
        } else {
          if (
            data.block_path + data.id + data.extend_id ==
            dbEntity.block_path + dbEntity.id + dbEntity.extend_id
          ) {
            this.m_context.SelectionManager.RemoveSelect(data);
            return true;
          }
        }
      }
    }

    return false;
  }
  // 通过extendid获取数据
  public passExtendIdGetData(extendId: string) {
    let data: (DbLine | DbMesh)[] = [];
    let { Lines, Meshs } = this.m_context.ModelManager;
    for (const key in Lines) {
      const lines = Lines[key].lines;
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].extend_id && lines[i].extend_id == extendId) {
          data.push(lines[i]);
        }
      }
    }
    for (const key in Meshs) {
      const meshs = Meshs[key].meshs;
      for (let i = 0; i < meshs.length; i++) {
        if (meshs[i].extend_id && meshs[i].extend_id == extendId) {
          data.push(meshs[i]);
        }
      }
    }

    return data;
  }

  // 通过起点和终点获取虚线点位
  public PassStartAndEndPointGetDashPoints(
    start: THREE.Vector3,
    end: THREE.Vector3,
    dashSize: number = 250,
    gapSize: number = 100
  ) {
    let sumSize = dashSize + gapSize;
    let distance = start.distanceTo(end) * 1000;
    let lineCurve = new THREE.LineCurve(
      new THREE.Vector2(start.x, start.y),
      new THREE.Vector2(end.x, end.y)
    );
    let segmentsNum = Math.ceil(distance / sumSize);
    let points: THREE.Vector3[] = [];
    for (let i = 0; i < segmentsNum; i++) {
      let startP = lineCurve.getPoint(i / segmentsNum);
      let segmentEndP = lineCurve.getPoint((i + 1) / segmentsNum);
      let segmentLineCurve = new THREE.LineCurve(
        new THREE.Vector2(startP.x, startP.y),
        new THREE.Vector2(segmentEndP.x, segmentEndP.y)
      );
      let endP = segmentLineCurve.getPoint(dashSize / sumSize);
      points.push(
        new THREE.Vector3(startP.x, startP.y, 0),
        new THREE.Vector3(endP.x, endP.y, 0)
      );
    }
    return points;
  }
  public GenerateUUID() {
    return generateUUID();
  }

  // 判断射线与哪些多边形数据相交
  public RayIntersectPolygon(raycaster: THREE.Raycaster) {
    let { PolygonDatas, PMesh } = this.m_context.ViewManager;
    if (!PMesh.visible) return [];
    let insertMeshS: THREE.Intersection[] = [];
    for (const value of PolygonDatas.values()) {
      let { shapeMesh } = value;
      let res = raycaster.intersectObject(shapeMesh);
      if (res.length) {
        insertMeshS.push(...res);
      }
    }
    // console.log(insertMeshS);
    return insertMeshS;
  }

  // 判断射线与哪些多边形数据线段相交
  public RayIntersectPolygonLine(raycaster: THREE.Raycaster, tol: number) {
    let { ray } = raycaster;
    let tolSq = tol * tol;
    let { PolygonDatas, PLine } = this.m_context.ViewManager;
    if (!PLine.visible) return [];
    for (const value of PolygonDatas.values()) {
      let { linePoints, shapeMesh } = value;
      let res = raycaster.intersectObject(shapeMesh);
      if (res.length) {
        let target = new THREE.Vector3();
        let targetOnSegment = new THREE.Vector3();
        for (let i = 0; i < linePoints.length - 1; i++) {
          let start = new THREE.Vector3(linePoints[i].x, linePoints[i].y, 0);
          let end = new THREE.Vector3(
            linePoints[i + 1].x,
            linePoints[i + 1].y,
            0
          );
          let distance = ray.distanceSqToSegment(
            start,
            end,
            target,
            targetOnSegment
          );
          if (distance <= tolSq) {
            let location = {
              x: Number(target.x),
              y: Number(target.y),
              z: Number(target.z),
            };
            let center = new THREE.Vector3(
              (start.x + end.x) * 0.5,
              (start.y + end.y) * 0.5,
              (start.z + end.z) * 0.5
            );
            let d1 = start.distanceTo(targetOnSegment);
            let d2 = center.distanceTo(targetOnSegment);
            let d3 = end.distanceTo(targetOnSegment);
            let position = IndsertPosition.None;
            let min = Math.min(d1, d2, d3);
            let adjust = targetOnSegment.clone();
            if (min <= tol) {
              if (min == d1) {
                position = IndsertPosition.Start;
                adjust = start.clone();
              }
              if (min == d2) {
                position = IndsertPosition.Center;
                adjust = center;
              }
              if (min == d3) {
                position = IndsertPosition.End;
                adjust = end.clone();
              }
            }
            let parms = new IndserObject(location, Math.sqrt(distance), "", [
              shapeMesh,
            ]);
            parms.position = position;
            if (parms.position == IndsertPosition.None) {
              parms.position = IndsertPosition.Insert;
            }
            parms.adjust = adjust;
            parms.geo = new THREE.Line3(start.clone(), end.clone());
            return [parms];
          }
        }
      }
    }
    return [];
  }

  // 组清除
  public GroupClear(group: THREE.Group) {
    for (let i = 0; i < group.children.length; i++) {
      let child = group.children[i];
      if (
        child instanceof THREE.Line ||
        child instanceof THREE.LineSegments ||
        child instanceof THREE.Mesh
      ) {
        child.removeFromParent();
        child.geometry.dispose();
        child.material.dispose();
        i--;
      } else if (child instanceof THREE.Group) {
        this.GroupClear(child);
      }
    }
    if (group.parent) {
      group.removeFromParent();
    }
  }
  // 对象清除
  public ObjectsClear(objs: THREE.Object3D[]) {
    for (let i = 0; i < objs.length; i++) {
      let obj = objs[i];
      if (
        obj instanceof THREE.Line ||
        obj instanceof THREE.LineSegments ||
        obj instanceof THREE.Mesh
      ) {
        obj.removeFromParent();
        obj.geometry.dispose();
        obj.material.dispose();
      }
    }
  }

  public LinesToModel(
    lines: DbLine[],
    lineMesh: THREE.LineSegments | THREE.Line
  ) {
    let position: number[] = [];
    let index = 0;
    let indexs: number[] = [];
    lines.forEach((item) => {
      item.points.forEach((p) => {
        position.push(p);
      });
      item.indexes?.forEach((v) => {
        indexs.push(v + index);
      });
      index += item.points.length / 3;
    });
    let posistionArray = new Float32Array(position);
    let lineGeo = lineMesh.geometry as THREE.BufferGeometry;
    lineGeo.setAttribute(
      "position",
      new THREE.BufferAttribute(posistionArray, 3)
    );
    lineGeo.setIndex(indexs);
    lineGeo.attributes.position.needsUpdate = true;
    lineGeo.computeBoundingBox();
    lineGeo.computeBoundingSphere();
  }

  public MeshToModel(meshs: DbMesh[], modelMesh: THREE.Mesh) {
    let posistion: number[] = [];
    meshs.forEach((geo) => {
      geo.vertexs?.forEach((v) => {
        posistion.push(v);
      });
    });
    let posistionArray = new Float32Array(posistion);
    let meshGeo = modelMesh.geometry as THREE.BufferGeometry;
    meshGeo.setAttribute(
      "position",
      new THREE.BufferAttribute(posistionArray, 3)
    );
    meshGeo.attributes.position.needsUpdate = true;
    meshGeo.computeBoundingBox();
    meshGeo.computeBoundingSphere();
  }

  // 根据起始点是终止点获取对象点集
  public PassStartPointAndEndPointGetSelectPoint(
    select: DbLine | DbMesh,
    start: THREE.Vector3,
    end: THREE.Vector3
  ) {
    let selectPoints: THREE.Vector3[] = [];
    if (select instanceof DbLine) {
      let points = [...select.points];
      let indexes = [...select.indexes];
      //虚线
        let {
          index: startIndex,
          isLine: startIsLine,
          castPoint: startCastPoint,
        } = this.PassPointGetIndexAndCastPoint(start, points);
        let {
          index: endIndex,
          isLine: endIsLine,
          castPoint: endCastPoint,
        } = this.PassPointGetIndexAndCastPoint(end, points);

        if (!startIsLine) {
          startIndex += 6;
        }
        // console.log(startIndex, endIndex, points);
        for (let i = startIndex; i < endIndex + 6; i += 3) {
          selectPoints.push(
            new THREE.Vector3(points[i], points[i + 1], points[i + 2])
          );
        }
        if (startIsLine) {
          selectPoints[0] = startCastPoint;
        }

        if (endIsLine) {
          selectPoints[selectPoints.length - 1] = endCastPoint;
        }
    } else if (select instanceof DbMesh) {
    }

    return selectPoints;
  }

  private passPointGetIndex(point: THREE.Vector3, points: number[]) {
    let index = -1;
    let isLine = false;
    for (let i = 0; i < points.length; i += 6) {
      let pS = new THREE.Vector3(points[i], points[i + 1], points[i + 2]);
      let pE = new THREE.Vector3(points[i + 3], points[i + 4], points[i + 5]);
      let nextPS = new THREE.Vector3(
        points[i + 6],
        points[i + 7],
        points[i + 8]
      );
      let line3 = new THREE.Line3(pS, pE);
      let res = line3.closestPointToPointParameter(point, false);
      if (res >= 0 && res <= 1) {
        index = i;
        isLine = true;
        break;
      } else if (points[i + 6]) {
        //虚线间隙检测
        let v1 = pE.clone().sub(point);
        let v2 = nextPS.clone().sub(point);
        if (
          Math.abs(v1.clone().cross(v2).z) <= 0.001 &&
          v1.clone().dot(v2) < 0
        ) {
          index = i;
          break;
        }
      } else {
        // 两端检测
        let dis1 = point.distanceTo(pS);
        let dis2 = point.distanceTo(pE);
        index = i;
        if (dis1 < dis2) {
          index = 0;
        }
        isLine = true;
        break;
      }
    }

    return { index, isLine };
  }

  public PassPointGetIndexAndCastPoint(point: THREE.Vector3, points: number[]) {
    let index = -1;
    let isLine = false;
    let castPoint = new THREE.Vector3();
    let originPoint = new THREE.Vector3(points[0], points[1], points[2]);
    let endPoint = new THREE.Vector3(
      points[points.length - 3],
      points[points.length - 2],
      points[points.length - 1]
    );
    for (let i = 0; i < points.length; i += 6) {
      let pS = new THREE.Vector3(points[i], points[i + 1], points[i + 2]);
      let pE = new THREE.Vector3(points[i + 3], points[i + 4], points[i + 5]);
      let closesPoint = new THREE.Vector3();
      let line3 = new THREE.Line3(pS, pE);
      line3.closestPointToPoint(point, true, closesPoint);
      let res = line3.closestPointToPointParameter(point, false);
      if (res >= 0 && res <= 1) {
        index = i;
        isLine = true;
        castPoint.copy(closesPoint);
        break;
      } else if (points[i + 6]) {
        let nextPS = new THREE.Vector3(
          points[i + 6],
          points[i + 7],
          points[i + 8]
        );
        //虚线间隙检测
        let line3 = new THREE.Line3(pE, nextPS);
        line3.closestPointToPoint(point, false, closesPoint);
        let res1 = line3.closestPointToPointParameter(closesPoint, false);
        if (res1 >= 0 && res1 <= 1) {
          index = i;
          castPoint.copy(pE);
          break;
        }
      }
    }
    if (index == -1) {
      // 传入的点在点集之外
      let dis1 = point.distanceTo(originPoint);
      let dis2 = point.distanceTo(endPoint);
      if (dis1 < dis2) {
        index = 0;
        castPoint.copy(originPoint);
      } else {
        index = points.length - 6;
        castPoint.copy(endPoint);
      }
      isLine = true;
    }
    // console.log(index, "index");
    return { index, isLine, castPoint };
  }

  public GetLayerLineTypeName(data: DbLine | DbMesh) {
    let layerName = this.m_context.ModelManager.GetLayerById(
      data.layerId
    )?.Name;
    if (layerName) {
      let sampleData =
        this.m_context.ModelManager.GetSampleDataByLayerName(layerName);
      return sampleData?.LayerLineTypeName;
    }
  }

  public VerifyLayerNameOrDataType(
    data: DbLine | DbMesh,
    accordLayerNames: string[]
  ) {
    let estimateResult = false;
    let layer = this.m_context.ModelManager.GetLayerById(data.layerId);
    if (!layer) return estimateResult;
    if (accordLayerNames && accordLayerNames?.length) {
      if (accordLayerNames.includes(layer.Name)) {
        estimateResult = true;
      }
    } else {
      estimateResult = this.m_context.ToolsManager.EstimateSelectType(
        data,
        [SampleLayerType.Line],
        [Linearity.Continuous, Linearity.Dash]
      );
    }
    return estimateResult;
  }
}

export { ToolsManger };
